import {useState, useEffect, useMemo, useRef} from "react";
import {createListCollection, useCombobox, useFilter} from "@chakra-ui/react";
import {flushSync} from "react-dom";
import uuid4 from "uuid4";

const NEW_PROVIDER_VALUE = "[[new]]"

const createBodyNode = (type = "string") => ({
    id: uuid4(),
    key: "",
    type,
    required: false,
    defaultValue: "",
    children: [],
})

const removeNodeById = (nodes, id) =>
    nodes.filter(n => n.id !== id).map(n => ({...n, children: removeNodeById(n.children, id)}))

const updateNodeById = (nodes, id, field, value) =>
    nodes.map(n => n.id === id
        ? {...n, [field]: value}
        : {...n, children: updateNodeById(n.children, id, field, value)}
    )

const addChildToNode = (nodes, parentId, child) =>
    nodes.map(n => n.id === parentId
        ? {...n, children: [...n.children, child]}
        : {...n, children: addChildToNode(n.children, parentId, child)}
    )

const jsonValueToNode = (key, value) => {
    if (Array.isArray(value)) {
        const child = value.length > 0 ? jsonValueToNode("", value[0]) : null
        return { ...createBodyNode("array"), key, children: child ? [child] : [] }
    }
    if (value !== null && typeof value === "object") {
        const children = Object.entries(value).map(([k, v]) => jsonValueToNode(k, v))
        return { ...createBodyNode("object"), key, children }
    }
    if (typeof value === "number")  return { ...createBodyNode("number"),  key }
    if (typeof value === "boolean") return { ...createBodyNode("boolean"), key }
    return { ...createBodyNode("string"), key }
}

const jsonToBodySchema = (json) =>
    Object.entries(json).map(([k, v]) => jsonValueToNode(k, v))

const createEmptyParam = () => ({
    id: uuid4(),
    name: "",
    type: "",
    defaultValue: "",
    required: false
})

const useEndpointForm = ({endpoint, endpoints, isOpen}) => {
    const [name, setName] = useState("")
    const [tag, setTag] = useState("")
    const [provider, setProvider] = useState("")
    const [description, setDescription] = useState("")
    const [method, setMethod] = useState(["GET"])
    const [url, setUrl] = useState("")
    const [params, setParams] = useState([createEmptyParam()])
    const [bodySchema, setBodySchema] = useState([])

    const {contains} = useFilter({sensitivity: "base"})

    const baseProviderItems = useMemo(
        () => [...new Set((endpoints ?? []).map(e => e.provider).filter(Boolean))].map(p => ({label: p, value: p})),
        [endpoints]
    )
    const [providerItems, setProviderItems] = useState(baseProviderItems)
    const providerItemsRef = useRef(baseProviderItems)
    const [providerSelected, setProviderSelected] = useState([])

    useEffect(() => {
        providerItemsRef.current = baseProviderItems
        setProviderItems(baseProviderItems)
    }, [baseProviderItems])

    const providerCollection = useMemo(() =>
        createListCollection({
            items: providerItems,
            itemToString: (item) => item.label,
            itemToValue: (item) => item.value,
        }), [providerItems])

    const isValidNewProvider = (inputValue) => {
        const exactMatch = providerItemsRef.current.some(
            item => item.label.toLowerCase() === inputValue.toLowerCase()
        )
        return !exactMatch && inputValue.trim().length > 0
    }

    const filterProviderItems = (query) => {
        if (isValidNewProvider(query)) {
            const newItem = {label: query, value: NEW_PROVIDER_VALUE}
            const filtered = providerItemsRef.current.filter(item => contains(item.label, query))
            setProviderItems([newItem, ...filtered])
            return
        }
        if (query.trim().length === 0) {
            setProviderItems(providerItemsRef.current)
        } else {
            setProviderItems(providerItemsRef.current.filter(item => contains(item.label, query)))
        }
    }

    const providerCombobox = useCombobox({
        collection: providerCollection,
        allowCustomValue: true,
        value: providerSelected,
        onInputValueChange: ({inputValue, reason}) => {
            if (reason === "input-change" || reason === "item-select") {
                flushSync(() => filterProviderItems(inputValue))
            }
        },
        onOpenChange({reason}) {
            if (reason === "trigger-click") {
                setProviderItems(providerItemsRef.current)
            }
        },
        onValueChange({value}) {
            const inputValue = providerCombobox.inputValue
            const resolved = value.map(v => v === NEW_PROVIDER_VALUE ? inputValue : v)
            setProviderSelected(resolved)
            setProvider(resolved[0] ?? "")
            if (value.includes(NEW_PROVIDER_VALUE)) {
                const newItem = {label: inputValue, value: inputValue}
                const filtered = providerItemsRef.current.filter(i => i.value !== NEW_PROVIDER_VALUE)
                providerItemsRef.current = [newItem, ...filtered]
                setProviderItems(providerItemsRef.current)
            }
        },
    })

    const loadEndpoint = (endpoint) => {
        setName(endpoint.name)
        setTag(endpoint.tag ?? "")
        setProvider(endpoint.provider ?? "")
        setProviderSelected(endpoint.provider ? [endpoint.provider] : [])
        setDescription(endpoint.description ?? "")
        setMethod([endpoint.method])
        setUrl(endpoint.url)
        setParams(endpoint.params?.length
            ? endpoint.params.map(p => ({...p, id: uuid4()}))
            : [createEmptyParam()])
        setBodySchema(endpoint.bodySchema ?? [])
    }

    const initializeNew = () => {
        setName("")
        setTag("")
        setProvider("")
        setProviderSelected([])
        setDescription("")
        setMethod(["GET"])
        setUrl("")
        setParams([createEmptyParam()])
        setBodySchema([])
    }

    useEffect(() => {
        if (endpoint) {
            loadEndpoint(endpoint);
        } else {
            initializeNew();
        }
        setProviderItems(providerItemsRef.current)
    }, [endpoint, isOpen])

    const handleParamChange = (id, field, value) => {
        setParams(prev => prev.map(p => p.id === id ? {...p, [field]: value} : p))
    }

    const handleAddParam = () => {
        setParams(prev => [...prev, createEmptyParam()])
    }

    const handleRemoveParam = (id) => {
        setParams(prev => {
            if (prev.length <= 1) return prev
            return prev.filter(p => p.id !== id)
        })
    }

    const addRootBodyNode = () => setBodySchema(prev => [...prev, createBodyNode()])

    const addChildBodyNode = (parentId) =>
        setBodySchema(prev => addChildToNode(prev, parentId, createBodyNode()))

    const removeBodyNode = (id) =>
        setBodySchema(prev => removeNodeById(prev, id))

    const updateBodyNode = (id, field, value) =>
        setBodySchema(prev => updateNodeById(prev, id, field, value))

    const importBodySchemaFromJson = (jsonString) => {
        try {
            const parsed = JSON.parse(jsonString)
            if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) return false
            setBodySchema(jsonToBodySchema(parsed))
            return true
        } catch {
            return false
        }
    }

    const buildSavePayload = () => ({
        ...(endpoint ? {id: endpoint.id} : {}),
        name,
        tag,
        provider,
        description,
        method: method[0],
        url,
        params,
        bodySchema,
    })

    const clearProvider = () => {
        setProvider("")
        setProviderSelected([])
    }

    return {
        name, setName,
        tag, setTag,
        description, setDescription,
        method, setMethod,
        url, setUrl,
        provider,
        providerCombobox,
        providerCollection,
        clearProvider,
        params,
        handleParamChange,
        handleAddParam,
        handleRemoveParam,
        bodySchema,
        addRootBodyNode,
        addChildBodyNode,
        removeBodyNode,
        updateBodyNode,
        importBodySchemaFromJson,
        buildSavePayload,
        NEW_PROVIDER_VALUE,
    }
}

export default useEndpointForm;
